import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import digitalTwin from "../assets/screenshots/My Shipment Digital Twin.png";
import blockchain from "../assets/screenshots/Blockchain Explorer.png";
import iotDevices from "../assets/screenshots/IoT Devices.png";
import iotMap from "../assets/screenshots/Iot Map.png";
import forecast from "../assets/screenshots/Demand Forecast.png";
import iotLive from "../assets/screenshots/IoT Live Control Center.png";

const screenshots = [
    [
        "Digital Twin",
        digitalTwin,
        "Virtual shipment monitoring with live operational state."
    ],
    [
        "Blockchain Explorer",
        blockchain,
        "Immutable blockchain audit trail for logistics operations."
    ],
    [
        "IoT Devices",
        iotDevices,
        "Real-time monitoring of logistics sensors and gateways."
    ],
    [
        "IoT Map",
        iotMap,
        "Interactive visualization of connected logistics assets."
    ],
    [
        "Demand Forecast",
        forecast,
        "Shipment demand prediction using historical logistics activity."
    ],
    [
        "IoT Live Control Center",
        iotLive,
        "Operational monitoring dashboard for intelligent logistics."
    ],
];

const modules = [
    [
        "🔐",
        "Identity-Based Security",
        "Secure authentication, authorization and encrypted data exchange."
    ],
    [
        "🤖",
        "Artificial Intelligence",
        "Anomaly detection, prediction and intelligent decision support."
    ],
    [
        "📡",
        "IoT Monitoring",
        "Live telemetry from logistics devices, sensors and shipment trackers."
    ],
    [
        "🛰️",
        "Digital Twin",
        "Virtual shipment representation with operational and security state."
    ],
];

const GuestPage = () => {
    const [selectedIndex, setSelectedIndex] = useState(null);

    const selectedImage =
        selectedIndex !== null ? screenshots[selectedIndex] : null;

    const closeModal = () => setSelectedIndex(null);

    const showPrev = () => {
        setSelectedIndex((prev) =>
            prev === 0 ? screenshots.length - 1 : prev - 1
        );
    };

    const showNext = () => {
        setSelectedIndex((prev) =>
            prev === screenshots.length - 1 ? 0 : prev + 1
        );
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedIndex === null) return;

            if (e.key === "Escape") closeModal();
            if (e.key === "ArrowLeft") showPrev();
            if (e.key === "ArrowRight") showNext();
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [selectedIndex]);

    return (
        <div className="w-screen min-h-screen bg-slate-50 text-slate-900 overflow-x-hidden">

            {/* HERO */}
            <section className="bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white">
                <div className="max-w-screen-2xl mx-auto px-8 xl:px-12 py-28 grid lg:grid-cols-2 gap-16 items-center">

                    <div>
                        <p className="uppercase tracking-[0.25em] text-blue-300 font-bold mb-5">
                            Secure Transparent Data Exchange
                        </p>

                        <h1 className="text-5xl lg:text-7xl font-black mb-6">
                            IDLogicChain
                        </h1>

                        <p className="text-xl text-blue-100 leading-relaxed max-w-3xl">
                            Research platform for intelligent logistics integrating
                            Artificial Intelligence, Blockchain, Identity-Based Security,
                            IoT telemetry and Digital Twin technologies.
                        </p>

                        <div className="flex flex-wrap gap-4 mt-10">
                            <Link
                                to="/login"
                                className="bg-white text-blue-950 px-8 py-4 rounded-xl font-bold shadow hover:bg-slate-100"
                            >
                                Login
                            </Link>

                            <Link
                                to="/register"
                                className="bg-green-500 text-white px-8 py-4 rounded-xl font-bold shadow hover:bg-green-600"
                            >
                                Register
                            </Link>

                            <Link
                                to="/showcase"
                                className="border border-blue-300 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/10"
                            >
                                Demo
                            </Link>
                        </div>
                    </div>

                    <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
                        <h2 className="text-2xl font-bold mb-8">
                            Platform Status
                        </h2>

                        <div className="grid grid-cols-2 gap-5">
                            <StatusCard title="AI" value="Enabled" />
                            <StatusCard title="Digital Twin" value="Online" />
                            <StatusCard title="Blockchain" value="Verified" />
                            <StatusCard title="IoT" value="Online" />
                        </div>

                        <p className="text-green-300 font-semibold mt-8">
                            ● Research prototype successfully demonstrated
                        </p>
                    </div>

                </div>
            </section>

            {/* METRICS */}
            <Section>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Metric
                        icon="🤖"
                        title="Artificial Intelligence"
                        value="Enabled"
                        text="Prediction and intelligent decision support"
                    />

                    <Metric
                        icon="📡"
                        title="IoT Devices"
                        value="Online"
                        text="Telemetry active"
                    />

                    <Metric
                        icon="⛓️"
                        title="Blockchain"
                        value="Verified"
                        text="Immutable audit"
                    />

                    <Metric
                        icon="🛰️"
                        title="Digital Twin"
                        value="Online"
                        text="Virtual logistics monitoring"
                    />
                </div>
            </Section>

            {/* MODULES */}
            <Section white title="Platform Modules">
                <p className="text-center text-slate-600 max-w-4xl mx-auto mb-14 text-lg">
                    IDLogicChain combines intelligent logistics, blockchain verification,
                    artificial intelligence, IoT telemetry and Digital Twin technologies
                    into one integrated platform.
                </p>

                <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8">
                    {modules.map(([icon, title, text]) => (
                        <FeatureCard
                            key={title}
                            icon={icon}
                            title={title}
                            text={text}
                        />
                    ))}
                </div>
            </Section>

            {/* ARCHITECTURE */}
            <Section title="Platform Architecture">
                <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-10">

                    <div className="grid lg:grid-cols-5 gap-6 text-center font-bold items-center">
                        <ArchBox
                            text="React SPA"
                            color="bg-blue-100 text-blue-900"
                        />

                        <div className="hidden lg:block text-5xl">
                            ➜
                        </div>

                        <ArchBox
                            text="ASP.NET Core API"
                            color="bg-green-100 text-green-900"
                        />

                        <div className="hidden lg:block text-5xl">
                            ➜
                        </div>

                        <ArchBox
                            text="SQL Server"
                            color="bg-purple-100 text-purple-900"
                        />
                    </div>

                    <div className="grid md:grid-cols-4 gap-6 mt-10">
                        {[
                            "ML.NET",
                            "Blockchain Logs",
                            "IoT Devices",
                            "Digital Twin"
                        ].map((item) => (
                            <div
                                key={item}
                                className="bg-slate-50 rounded-2xl p-6 shadow text-center font-bold"
                            >
                                {item}
                            </div>
                        ))}
                    </div>

                </div>
            </Section>

            {/* RESEARCH */}
            <Section white title="Research & Innovation">
                <div className="max-w-4xl mx-auto">
                    <InfoCard
                        title="Scientific Contribution"
                        text="The project demonstrates how Artificial Intelligence, Blockchain, Identity-Based Encryption, IoT telemetry and Digital Twin technologies can operate together inside one secure logistics ecosystem."
                    />
                </div>
            </Section>

            {/* PLATFORM DEMONSTRATION */}
            <Section title="Platform Demonstration">
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">

                    {screenshots.map(([title, image, text], index) => (
                        <div
                            key={title}
                            className="bg-white rounded-3xl overflow-hidden shadow-xl border border-slate-200 hover:shadow-2xl transition"
                        >
                            <button
                                type="button"
                                onClick={() => setSelectedIndex(index)}
                                className="block w-full bg-transparent p-0 text-left overflow-hidden"
                            >
                                <img
                                    src={image}
                                    alt={title}
                                    className="w-full h-56 object-cover cursor-pointer hover:scale-105 transition duration-300"
                                />
                            </button>

                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-2">
                                    {title}
                                </h3>

                                <p className="text-slate-600 text-sm leading-relaxed">
                                    {text}
                                </p>

                                <button
                                    type="button"
                                    onClick={() => setSelectedIndex(index)}
                                    className="mt-4 text-blue-700 font-bold hover:underline bg-transparent p-0"
                                >
                                    Open screenshot
                                </button>
                            </div>
                        </div>
                    ))}

                </div>
            </Section>

            {/* PLATFORM STATUS */}
            <section className="bg-gradient-to-r from-blue-950 to-slate-900 text-white py-20">
                <div className="max-w-screen-2xl mx-auto px-8 xl:px-12 text-center">

                    <h2 className="text-4xl font-black mb-12">
                        Platform Status
                    </h2>

                    <div className="grid md:grid-cols-4 gap-10">
                        <StatusBig
                            value="✔"
                            text="Blockchain Ready"
                            color="text-green-400"
                        />

                        <StatusBig
                            value="AI"
                            text="Prediction Enabled"
                            color="text-blue-300"
                        />

                        <StatusBig
                            value="IoT"
                            text="Telemetry Active"
                            color="text-yellow-300"
                        />

                        <StatusBig
                            value="Twin"
                            text="Digital Twin Online"
                            color="text-purple-300"
                        />
                    </div>

                </div>
            </section>

            {/* DEVELOPMENT ROADMAP */}
            <Section white title="Development Roadmap">
                <div className="grid md:grid-cols-5 gap-6">

                    {[
                        ["2025", "Research"],
                        ["2026", "Platform Demo"],
                        ["Next", "Further Development"],
                        ["Future", "Industrial Pilot"],
                        ["Vision", "Smart Logistics"],
                    ].map(([year, title]) => (
                        <div
                            key={title}
                            className="bg-slate-100 rounded-2xl shadow p-8 text-center"
                        >
                            <div className="text-blue-700 font-black text-2xl">
                                {year}
                            </div>

                            <div className="mt-3 font-semibold">
                                {title}
                            </div>
                        </div>
                    ))}

                </div>
            </Section>

            {/* EXPLORE */}
            <section className="bg-blue-950 text-white py-20">
                <div className="max-w-screen-xl mx-auto px-8 text-center">

                    <h2 className="text-5xl font-black mb-6">
                        Explore IDLogicChain
                    </h2>

                    <p className="text-blue-100 text-xl leading-relaxed mb-10">
                        Intelligent logistics platform integrating Artificial Intelligence,
                        Blockchain, Digital Twin, IoT telemetry and Identity-Based Security
                        technologies.
                    </p>

                    <div className="flex justify-center gap-6 flex-wrap">
                        <Link
                            to="/login"
                            className="bg-white text-blue-900 px-8 py-4 rounded-xl font-bold hover:bg-slate-100"
                        >
                            Login
                        </Link>

                        <Link
                            to="/register"
                            className="bg-green-500 text-white px-8 py-4 rounded-xl font-bold hover:bg-green-600"
                        >
                            Register
                        </Link>
                    </div>

                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-slate-950 text-slate-300">
                <div className="max-w-screen-2xl mx-auto px-8 xl:px-12 py-12 grid md:grid-cols-3 gap-10">

                    <div>
                        <h3 className="text-white text-2xl font-bold mb-4">
                            🛡 IDLogicChain
                        </h3>

                        <p>
                            Secure Transparent Data Exchange Platform
                        </p>

                        <p className="mt-4 text-slate-400">
                            AI • Blockchain • IoT • Digital Twin • Identity-Based Security
                        </p>
                    </div>

                    <div>
                        <h3 className="text-white font-bold mb-4">
                            Technologies
                        </h3>

                        <p>ASP.NET Core</p>
                        <p>React</p>
                        <p>SQL Server</p>
                        <p>ML.NET</p>
                        <p>Docker</p>
                    </div>

                    <div>
                        <h3 className="text-white font-bold mb-4">
                            Project Status
                        </h3>

                        <p>✔ Master's Thesis Completed</p>
                        <p>✔ Research Prototype</p>
                        <p>✔ Cloud Deployment</p>
                        <p>✔ Platform Demonstration</p>
                    </div>

                </div>
            </footer>

            {/* IMAGE MODAL */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6"
                    onClick={closeModal}
                >
                    <button
                        type="button"
                        onClick={closeModal}
                        className="absolute top-6 right-8 text-white text-5xl font-black bg-transparent hover:text-slate-300"
                    >
                        ×
                    </button>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            showPrev();
                        }}
                        className="absolute left-6 top-1/2 -translate-y-1/2 text-white text-5xl font-black bg-black/30 rounded-full w-16 h-16 hover:bg-black/60"
                    >
                        ‹
                    </button>

                    <div
                        className="max-w-[95vw] max-h-[95vh]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={selectedImage[1]}
                            alt={selectedImage[0]}
                            className="max-w-[95vw] max-h-[82vh] rounded-2xl shadow-2xl object-contain"
                        />

                        <div className="text-white text-center mt-4">
                            <h3 className="text-2xl font-bold">
                                {selectedImage[0]}
                            </h3>

                            <p className="text-slate-300 mt-2">
                                {selectedImage[2]}
                            </p>

                            <p className="text-slate-400 text-sm mt-2">
                                Use ← → keys or buttons to browse. Press Esc to close.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            showNext();
                        }}
                        className="absolute right-6 top-1/2 -translate-y-1/2 text-white text-5xl font-black bg-black/30 rounded-full w-16 h-16 hover:bg-black/60"
                    >
                        ›
                    </button>
                </div>
            )}

        </div>
    );
};

/* =========================================================
   COMPONENTS
   ========================================================= */

const Section = ({ children, title, white = false }) => (
    <section className={`py-24 ${white ? "bg-white" : "bg-slate-100"}`}>
        <div className="max-w-screen-2xl mx-auto px-8 xl:px-12">

            {title && (
                <h2 className="text-3xl lg:text-4xl font-black text-center mb-12">
                    {title}
                </h2>
            )}

            {children}

        </div>
    </section>
);

const StatusCard = ({ title, value }) => (
    <div className="bg-white/10 rounded-2xl p-5 border border-white/10">
        <p className="text-blue-100">
            {title}
        </p>

        <h3 className="text-3xl font-black mt-2">
            {value}
        </h3>
    </div>
);

const Metric = ({ icon, title, value, text }) => (
    <div className="bg-white rounded-3xl p-7 shadow-lg border border-slate-200">
        <div className="text-5xl mb-4">
            {icon}
        </div>

        <p className="text-slate-500">
            {title}
        </p>

        <h3 className="text-2xl font-black mt-2">
            {value}
        </h3>

        <p className="text-slate-500 text-sm mt-2">
            {text}
        </p>
    </div>
);

const FeatureCard = ({ icon, title, text }) => (
    <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200 shadow hover:shadow-xl transition">
        <div className="text-5xl mb-5">
            {icon}
        </div>

        <h3 className="text-2xl font-bold mb-3">
            {title}
        </h3>

        <p className="text-slate-600 leading-relaxed">
            {text}
        </p>
    </div>
);

const ArchBox = ({ text, color }) => (
    <div className={`${color} rounded-2xl p-8 shadow font-black`}>
        {text}
    </div>
);

const InfoCard = ({ title, text }) => (
    <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200 shadow">
        <h3 className="text-3xl font-bold mb-5">
            {title}
        </h3>

        <p className="text-slate-700 leading-8">
            {text}
        </p>
    </div>
);

const StatusBig = ({ value, text, color }) => (
    <div className="text-center">
        <div className={`text-5xl font-black mb-4 ${color}`}>
            {value}
        </div>

        <p className="text-xl font-semibold">
            {text}
        </p>
    </div>
);

export default GuestPage;